/**
 * DocumentViewerModal — visualizador de documentos in-ERP em modal de tela cheia.
 *
 * Renderiza inline, 100% client-side (nenhum arquivo sai pra serviço de
 * terceiro), os formatos: PDF, imagens (png/jpg/gif/webp/svg/bmp), texto
 * (txt/xml/json/log/md), CSV e XLSX (como tabela), DOCX (via mammoth) e HTML
 * (contrato). Formatos sem preview caem num fallback honesto com botão de
 * download.
 *
 * Auth: documentos do próprio backend (o proxy `/api/crm/anexos/{id}/download`
 * e o /media) são buscados como blob com o JWT no header, renovando a sessão
 * uma vez em 401 — sem isso, um cockpit aberto além dos 15 min do access
 * mostrava "HTTP 401". NF-e (URL externa do gateway) e contrato (rota pública)
 * usam a URL direto no iframe.
 *
 * Acessibilidade/UX: role=dialog + aria-modal, fecha no X / ESC / clique no
 * backdrop, trava o scroll do body, devolve o foco ao elemento anterior.
 * z-index 1050 — acima do header global (100) e do SlideOverPanel (1000).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FiAlertCircle,
  FiDownload,
  FiExternalLink,
  FiFile,
  FiLoader,
  FiX,
} from "react-icons/fi";
import styled, { keyframes } from "styled-components";

import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  buscarArquivoComAuth,
  detectarKind,
  estrategia,
  urlAbsoluta,
} from "../../utils/documentos";

// Teto de tamanho pra visualizacao inline — evita travar o browser carregando
// arquivos gigantes em memoria (blob + arrayBuffer + parser). Acima disso, so
// download.
const TAMANHO_MAX_BYTES = 40 * 1024 * 1024; // 40 MB

// ---------- helpers locais ----------
// `detectarKind`, `estrategia`, `urlAbsoluta` e o fetch autenticado moraram
// aqui até o CRM-50; agora vivem em utils/documentos.js, porque o grid e as
// telas de anexo precisam da mesma régua.

function dispararDownload(href, nome) {
  const a = document.createElement("a");
  a.href = href;
  a.download = nome || "documento";
  a.target = "_blank";
  a.rel = "noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// Parsers carregados sob demanda (mammoth/xlsx só entram no bundle quando usados).
async function planilhaParaTabelas(input, tipo) {
  const XLSX = await import("xlsx");
  const wb = XLSX.read(input, { type: tipo }); // tipo: "array" (xlsx) | "string" (csv)
  return wb.SheetNames.map((nome) => ({
    nome,
    linhas: XLSX.utils.sheet_to_json(wb.Sheets[nome], {
      header: 1,
      blankrows: false,
      defval: "",
    }),
  }));
}

async function docxParaHtml(arrayBuffer) {
  const [{ default: mammoth }, { default: DOMPurify }] = await Promise.all([
    import("mammoth"),
    import("dompurify"),
  ]);
  const { value } = await mammoth.convertToHtml({ arrayBuffer });
  // Whitelist explicita (defesa em profundidade): so tags de texto/estrutura
  // que o mammoth gera. Remove img/svg/style/iframe e qualquer event handler.
  return DOMPurify.sanitize(value || "", {
    ALLOWED_TAGS: [
      "p", "br", "strong", "b", "em", "i", "u", "s", "sup", "sub",
      "h1", "h2", "h3", "h4", "h5", "h6", "blockquote",
      "ul", "ol", "li", "table", "thead", "tbody", "tr", "td", "th",
      "a", "span", "div", "pre", "code", "hr",
    ],
    ALLOWED_ATTR: ["href", "title", "class", "colspan", "rowspan"],
    ALLOW_DATA_ATTR: false,
  });
}

// ---------- styled ----------

const fadeIn = keyframes`from { opacity: 0 } to { opacity: 1 }`;
const popIn = keyframes`
  from { transform: scale(.97); opacity: 0 }
  to { transform: scale(1); opacity: 1 }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(8px, 2vw, 28px);
  background: rgba(11, 15, 26, 0.62);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  animation: ${fadeIn} 0.16s ease both;
`;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(1200px, 96vw);
  height: 92vh;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: var(--radius-lg, 16px);
  box-shadow: 0 24px 60px rgba(11, 15, 26, 0.32);
  overflow: hidden;
  animation: ${popIn} 0.2s cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--border, #e5e7eb);
  flex-shrink: 0;
  background: var(--surface, #fff);
`;

const DocIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: var(--accent-soft, rgba(91, 91, 246, 0.12));
  color: var(--accent, #5b5bf6);
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const Titulo = styled.div`
  font-family: var(--font-display, inherit);
  font-weight: 600;
  font-size: 15px;
  color: var(--ink, #0b0f1a);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Sub = styled.div`
  font-size: 12px;
  color: var(--ink-3, #6b7280);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Acoes = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const BotaoIcone = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 ${({ $somenteIcone }) => ($somenteIcone ? "0" : "13px")};
  width: ${({ $somenteIcone }) => ($somenteIcone ? "36px" : "auto")};
  justify-content: center;
  border-radius: 9px;
  border: 1px solid var(--border, #e5e7eb);
  background: ${({ $primary }) => ($primary ? "var(--accent, #5b5bf6)" : "transparent")};
  color: ${({ $primary }) => ($primary ? "#fff" : "var(--ink-2, #374151)")};
  border-color: ${({ $primary }) => ($primary ? "var(--accent, #5b5bf6)" : "var(--border, #e5e7eb)")};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;

  &:hover {
    background: ${({ $primary }) =>
      $primary ? "var(--accent-active, #4a4ad6)" : "var(--surface-2, #f3f4f6)"};
    color: ${({ $primary }) => ($primary ? "#fff" : "var(--ink, #0b0f1a)")};
  }
  &:focus-visible {
    outline: 2px solid var(--accent, #5b5bf6);
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  background: var(--surface-2, #f3f4f6);
  overflow: auto;
  display: flex;
  flex-direction: column;
`;

const Frame = styled.iframe`
  flex: 1;
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
`;

const ImagemWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 8px;
    box-shadow: 0 6px 24px rgba(11, 15, 26, 0.18);
    background: #fff;
  }
`;

const Texto = styled.pre`
  margin: 0;
  padding: 22px 26px;
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 12.5px;
  line-height: 1.6;
  color: var(--ink, #0b0f1a);
  white-space: pre-wrap;
  word-break: break-word;
  background: var(--surface, #fff);
  flex: 1;
`;

const DocxWrap = styled.div`
  flex: 1;
  padding: 32px clamp(18px, 5vw, 64px);
  background: var(--surface, #fff);
  color: var(--ink, #0b0f1a);
  font-size: 14px;
  line-height: 1.65;
  overflow: auto;

  h1, h2, h3 { font-family: var(--font-display, inherit); margin: 1.2em 0 0.5em; }
  p { margin: 0 0 0.8em; }
  table { border-collapse: collapse; margin: 1em 0; width: 100%; }
  td, th { border: 1px solid var(--border, #e5e7eb); padding: 6px 9px; }
  img { max-width: 100%; }
`;

const TabelaWrap = styled.div`
  flex: 1;
  overflow: auto;
  background: var(--surface, #fff);
  padding: 8px;
`;

const AbasPlanilha = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  padding: 8px 8px 12px;
  button {
    appearance: none;
    border: 1px solid var(--border, #e5e7eb);
    background: transparent;
    color: var(--ink-2, #374151);
    border-radius: 7px;
    padding: 5px 11px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
  }
  button.on {
    background: var(--accent-soft, rgba(91, 91, 246, 0.12));
    color: var(--accent, #5b5bf6);
    border-color: var(--accent, #5b5bf6);
  }
`;

const Tabela = styled.table`
  border-collapse: collapse;
  font-size: 12.5px;
  width: max-content;
  min-width: 100%;
  td, th {
    border: 1px solid var(--border, #e5e7eb);
    padding: 5px 10px;
    white-space: nowrap;
    color: var(--ink, #0b0f1a);
  }
  thead th {
    position: sticky;
    top: 0;
    background: var(--surface-2, #f3f4f6);
    font-weight: 600;
    text-align: left;
    z-index: 1;
  }
  tbody tr:nth-child(even) {
    background: var(--surface-2, rgba(0, 0, 0, 0.02));
  }
`;

const Centro = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  text-align: center;
  color: var(--ink-3, #6b7280);

  .ico {
    font-size: 34px;
    color: ${({ $erro }) => ($erro ? "var(--danger-text, #dc2626)" : "var(--ink-4, #9ca3af)")};
    display: flex;
  }
  .titulo {
    font-weight: 600;
    color: var(--ink-2, #374151);
    font-size: 15px;
  }
  .desc {
    font-size: 13px;
    max-width: 380px;
  }
`;

const giro = keyframes`to { transform: rotate(360deg) }`;
const Spinner = styled(FiLoader)`
  animation: ${giro} 0.9s linear infinite;
`;

// ---------- subcomponentes de conteúdo ----------

function ConteudoPlanilha({ tabelas }) {
  const [aba, setAba] = useState(0);
  const atual = tabelas[aba] || tabelas[0];
  if (!atual) return null;
  const linhas = atual.linhas || [];
  const [cabecalho, ...corpo] = linhas.length ? linhas : [[]];
  const maxCols = linhas.reduce((m, l) => Math.max(m, l.length), 0);
  const cols = Array.from({ length: maxCols });
  return (
    <>
      {tabelas.length > 1 ? (
        <AbasPlanilha>
          {tabelas.map((t, i) => (
            <button key={t.nome + i} type="button" className={i === aba ? "on" : ""} onClick={() => setAba(i)}>
              {t.nome}
            </button>
          ))}
        </AbasPlanilha>
      ) : null}
      <TabelaWrap>
        <Tabela>
          <thead>
            <tr>
              <th style={{ color: "var(--ink-4)" }}>#</th>
              {cols.map((_, c) => (
                <th key={c}>{(cabecalho && cabecalho[c]) ?? ""}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {corpo.map((linha, r) => (
              <tr key={r}>
                <td style={{ color: "var(--ink-4)", textAlign: "right" }}>{r + 1}</td>
                {cols.map((_, c) => (
                  <td key={c}>{linha[c] != null ? String(linha[c]) : ""}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Tabela>
      </TabelaWrap>
    </>
  );
}

// ---------- componente principal ----------

export default function DocumentViewerModal({ doc, onClose }) {
  const [estado, setEstado] = useState({ loading: true, erro: null });
  const [baixando, setBaixando] = useState(false);
  const containerRef = useRef(null);
  const previousFocusRef = useRef(null);
  const downloadUrlsRef = useRef([]);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const { request } = useAuth() || {};
  const toast = useToast();

  // O access token dura 15 min. Como o fetch do blob lê o Bearer direto do
  // localStorage (o `request` do AuthContext devolve JSON, não binário), um
  // cockpit aberto além desse prazo mostrava "HTTP 401" no visualizador.
  // `/api/me` passa pelo `request`, que tem o mutex de refresh e regrava o
  // token — depois disso o retry lê o token novo.
  const renovarSessao = useCallback(async () => {
    if (!request) return;
    await request("/api/me", { silentError: true });
  }, [request]);

  const kind = doc ? detectarKind(doc) : null;

  // Carrega o conteúdo (fetch blob com auth quando necessário) + parsers.
  useEffect(() => {
    if (!doc) return undefined;
    let cancelado = false;
    const urlsCriadas = [];
    const controller = new AbortController();
    setEstado({ loading: true, erro: null });

    (async () => {
      try {
        if (!doc.url) {
          throw new Error("Este documento não tem arquivo associado para visualizar.");
        }
        if (estrategia(doc, kind) === "direto") {
          if (!cancelado) setEstado({ loading: false, erro: null, direto: doc.url });
          return;
        }
        const blob = await buscarArquivoComAuth(doc.url, {
          signal: controller.signal,
          renovarSessao,
        });
        if (cancelado) return;
        if (blob.size > TAMANHO_MAX_BYTES) {
          throw new Error(
            `Arquivo grande demais para pré-visualizar (${Math.round(
              blob.size / 1048576
            )} MB, máximo 40 MB). Use o botão Baixar.`
          );
        }
        const objectUrl = URL.createObjectURL(blob);
        urlsCriadas.push(objectUrl);

        let texto = null;
        let tabelas = null;
        let html = null;
        try {
          if (kind === "text") {
            texto = await blob.text();
          } else if (kind === "csv") {
            tabelas = await planilhaParaTabelas(await blob.text(), "string");
          } else if (kind === "xlsx") {
            tabelas = await planilhaParaTabelas(await blob.arrayBuffer(), "array");
          } else if (kind === "docx") {
            html = await docxParaHtml(await blob.arrayBuffer());
          }
        } catch {
          throw new Error(
            `Não foi possível processar este arquivo (${String(
              kind
            ).toUpperCase()}). Ele pode estar corrompido — tente baixá-lo.`
          );
        }
        if (!cancelado) {
          setEstado({ loading: false, erro: null, blobUrl: objectUrl, texto, tabelas, html });
        }
      } catch (e) {
        if (cancelado || e?.name === "AbortError") return;
        // A mensagem vem do backend quando ele manda uma: é assim que o
        // "Arquivo indisponível — o anexo não está mais armazenado." (404 do
        // proxy) aparece no lugar do XML NoSuchKey da AWS.
        setEstado({ loading: false, erro: e?.message || "Não foi possível abrir o documento." });
      }
    })();

    return () => {
      cancelado = true;
      controller.abort();
      urlsCriadas.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [doc, kind, renovarSessao]);

  // ESC + scroll lock + foco + focus trap (WCAG 2.4.3 — TAB nao sai do modal).
  useEffect(() => {
    if (!doc) return undefined;
    previousFocusRef.current = document.activeElement;
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function onKey(e) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== "Tab") return;
      const root = containerRef.current;
      if (!root) return;
      const foco = root.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
      );
      if (!foco.length) {
        e.preventDefault();
        root.focus();
        return;
      }
      const primeiro = foco[0];
      const ultimo = foco[foco.length - 1];
      const ativo = document.activeElement;
      if (e.shiftKey && (ativo === primeiro || ativo === root || !root.contains(ativo))) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && ativo === ultimo) {
        e.preventDefault();
        primeiro.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    containerRef.current?.focus?.();

    return () => {
      document.body.style.overflow = overflowAnterior;
      window.removeEventListener("keydown", onKey);
      previousFocusRef.current?.focus?.();
    };
  }, [doc]);

  // Revoga os object URLs criados sob demanda no download, ao desmontar.
  useEffect(() => () => downloadUrlsRef.current.forEach(URL.revokeObjectURL), []);

  const baixar = useCallback(async () => {
    if (!doc?.url) return;
    setBaixando(true);
    try {
      if (estado.blobUrl) {
        dispararDownload(estado.blobUrl, doc.nome);
      } else if (estrategia(doc, detectarKind(doc)) === "blob") {
        const blob = await buscarArquivoComAuth(doc.url, { renovarSessao });
        const u = URL.createObjectURL(blob);
        downloadUrlsRef.current.push(u); // revogado no unmount
        dispararDownload(u, doc.nome);
      } else {
        dispararDownload(urlAbsoluta(doc.url), doc.nome);
      }
    } catch (e) {
      // Abrir em nova aba só resolve quando a URL se defende sozinha (gateway
      // fiscal, contrato público). No proxy autenticado a aba nova levaria 401
      // ou baixaria um JSON de erro: ali a falha vira mensagem.
      if (estrategia(doc, detectarKind(doc)) === "direto") {
        window.open(urlAbsoluta(doc.url), "_blank", "noreferrer");
      } else {
        toast.error(e?.message || "Não foi possível baixar o arquivo.");
      }
    } finally {
      setBaixando(false);
    }
  }, [doc, estado.blobUrl, renovarSessao, toast]);

  if (!doc) return null;

  const handleOverlay = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <Overlay onClick={handleOverlay} role="presentation">
      <Container
        ref={containerRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={`Visualizador de documento: ${doc.nome}`}
      >
        <Header>
          <DocIcon>
            <FiFile size={20} aria-hidden="true" />
          </DocIcon>
          <HeaderText>
            <Titulo role="heading" aria-level={1} title={doc.nome}>
              {doc.nome}
            </Titulo>
            <Sub>{[doc.fonte, doc.situacao].filter(Boolean).join(" · ")}</Sub>
          </HeaderText>
          <Acoes>
            <BotaoIcone type="button" onClick={baixar} disabled={baixando} $primary title="Baixar arquivo">
              <FiDownload size={15} aria-hidden="true" />
              {baixando ? "Baixando…" : "Baixar"}
            </BotaoIcone>
            <BotaoIcone type="button" onClick={onClose} $somenteIcone aria-label="Fechar" title="Fechar (Esc)">
              <FiX size={18} aria-hidden="true" />
            </BotaoIcone>
          </Acoes>
        </Header>
        <Body>
          <Conteudo doc={doc} kind={kind} estado={estado} onBaixar={baixar} />
        </Body>
      </Container>
    </Overlay>
  );
}

function Conteudo({ doc, kind, estado, onBaixar }) {
  if (estado.loading) {
    return (
      <Centro>
        <span className="ico">
          <Spinner size={30} aria-hidden="true" />
        </span>
        <div className="desc">Carregando documento…</div>
      </Centro>
    );
  }
  if (estado.erro) {
    return (
      <Centro $erro>
        <span className="ico">
          <FiAlertCircle aria-hidden="true" />
        </span>
        <div className="titulo">Não foi possível exibir</div>
        <div className="desc">{estado.erro}</div>
        <BotaoIcone type="button" onClick={onBaixar}>
          <FiDownload size={15} aria-hidden="true" /> Baixar arquivo
        </BotaoIcone>
      </Centro>
    );
  }

  // Fonte da URL a renderizar: blob (auth) ou direto (externo/rota pública).
  const src = estado.blobUrl || estado.direto;

  if (kind === "pdf") {
    return <Frame src={src} title={doc.nome} />;
  }
  if (kind === "image") {
    return (
      <ImagemWrap>
        <img src={src} alt={doc.nome} />
      </ImagemWrap>
    );
  }
  if (kind === "html") {
    // contrato: rota pública same-origin (precisa rodar o app) — sem sandbox restritivo.
    return <Frame src={src} title={doc.nome} />;
  }
  if (kind === "text") {
    if (estado.texto != null) return <Texto>{estado.texto}</Texto>;
    return <Frame src={src} title={doc.nome} />; // texto externo (ex.: XML do gateway)
  }
  if ((kind === "csv" || kind === "xlsx") && estado.tabelas) {
    return <ConteudoPlanilha tabelas={estado.tabelas} />;
  }
  if (kind === "docx" && estado.html != null) {
    // eslint-disable-next-line react/no-danger -- HTML já sanitizado com DOMPurify
    return <DocxWrap dangerouslySetInnerHTML={{ __html: estado.html }} />;
  }

  // Fallback honesto para formatos sem preview (.doc, .dwg, .zip, etc).
  return (
    <Centro>
      <span className="ico">
        <FiFile aria-hidden="true" />
      </span>
      <div className="titulo">Pré-visualização não disponível</div>
      <div className="desc">
        Este tipo de arquivo não tem visualização inline. Você pode baixá-lo para abrir no
        aplicativo apropriado.
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <BotaoIcone type="button" onClick={onBaixar} $primary>
          <FiDownload size={15} aria-hidden="true" /> Baixar arquivo
        </BotaoIcone>
        {src ? (
          <BotaoIcone as="a" href={src} target="_blank" rel="noreferrer">
            <FiExternalLink size={15} aria-hidden="true" /> Abrir em nova aba
          </BotaoIcone>
        ) : null}
      </div>
    </Centro>
  );
}
